// The bots a commissioner chooses from, and the asks that seat them
// (AI_MANAGERS.md §3.1, scenario B7, B-459).
//
// THE CAST IS SERVED, not bundled into a client: a second frontend chooses
// from the same characters, with the same words for each band.

import { z } from 'zod';

/** How well a bot plays, in the five bands people choose from (AI_MANAGERS.md §4.2). */
export const SKILL_BANDS = ['along', 'casual', 'solid', 'sharp', 'ruthless'] as const;
export const SkillBandId = z.enum(SKILL_BANDS);
export type SkillBandId = z.infer<typeof SkillBandId>;

const Dial = z.number().min(0).max(1);
/**
 * A bot's personality, five dials from 0 to 1 (AI_MANAGERS.md §4.4): how much
 * it talks, how bold it is with a player on the injury report, how often it
 * trades and how hard it bids, how attached it is to its own draft, and how
 * often it looks at its team.
 */
export const BotDials = z.object({
  trashTalk: Dial,
  aggression: Dial,
  risk: Dial,
  loyalty: Dial,
  activity: Dial,
});
export type BotDials = z.infer<typeof BotDials>;

/** One character of the cast, as the chooser shows it. */
export const BotCastMember = z.object({
  /** Stable for good: the id a seat stores (`presetId`). */
  id: z.string(),
  name: z.string(),
  /** One line in its own voice. */
  voice: z.string(),
  /** How it plays, in a sentence or two. */
  plays: z.string(),
  /** What a seat it takes is called. */
  teamName: z.string(),
  /** The band it plays at unless a seat is given another. */
  band: SkillBandId,
  /** How much it talks, from its dial: in the chat and on the matchup. */
  talks: z.enum(['rarely', 'sometimes', 'a lot']),
  /**
   * How it answers a trade at each band, with its own dials, in a phrase:
   * *takes a fair deal and haggles* (B-182). By band because a seat may be
   * given another, which moves how far ahead it must come out. Added
   * 2026-10-22.
   */
  trades: z.object({
    along: z.string(),
    casual: z.string(),
    solid: z.string(),
    sharp: z.string(),
    ruthless: z.string(),
  }),
  /** Its dials as its file sets them, for a chooser that adjusts them. Added 2026-10-19. */
  dials: BotDials,
});
export type BotCastMember = z.infer<typeof BotCastMember>;

/** `GET /api/bots`: the cast, and the bands with what each promises. */
export const BotCastView = z.object({
  cast: z.array(BotCastMember),
  bands: z.array(z.object({ id: SkillBandId, label: z.string(), promise: z.string() })),
});
export type BotCastView = z.infer<typeof BotCastView>;

/**
 * `POST /api/leagues/:id/seats/:teamId/bot`, its body optional: a character
 * by id, a band, either or neither. Neither is the house's pick at its own
 * band. Added 2026-10-19; the route took no body before.
 */
export const SeatBotRequest = z.object({
  presetId: z.string().min(1).optional(),
  band: SkillBandId.optional(),
  /**
   * The commissioner's own dials for this seat, any of the five, over the
   * character's (B-462). Stored on the seat and read before its file's.
   */
  dials: BotDials.partial().optional(),
});
export type SeatBotRequest = z.infer<typeof SeatBotRequest>;

/** `POST /api/leagues/:id/seats/bots`: every open seat, the bands around this one. */
export const FillSeatsRequest = z.object({ band: SkillBandId });
export type FillSeatsRequest = z.infer<typeof FillSeatsRequest>;
